import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight,
  Phone, 
  Mail, 
  MapPin, 
  FileText, 
  Shield, 
  CreditCard,
  HelpCircle,
  Users,
  Info,
  Clock,
  Package,
  ExternalLink,
  Headphones
} from 'lucide-react';

/**
 * PWA More Menu - Vollständiges Menü mit allen Links
 * Alle Bereiche: Service, Rechtliches, Über uns, Kontakt
 */
const PWAMoreMenu = () => {
  const navigate = useNavigate();

  const menuSections = [
    {
      title: 'Kontakt',
      items: [
        { 
          icon: Phone, 
          label: 'Anrufen', 
          subtitle: '02041 9877499',
          href: 'tel:+4920419877499', 
          external: true,
          highlight: true
        },
        { 
          icon: Mail, 
          label: 'E-Mail schreiben', 
          subtitle: 'info@jannesmann.de',
          href: 'mailto:info@jannesmann.de', 
          external: true 
        },
        { 
          icon: MapPin, 
          label: 'Standort & Kontakt', 
          to: '/kontakt' 
        },
      ]
    },
    {
      title: 'Service',
      items: [
        { icon: HelpCircle, label: 'FAQ', subtitle: 'Häufige Fragen', to: '/faq' },
        { icon: Shield, label: 'Garantiebedingungen', subtitle: '12 Monate Garantie', to: '/garantiebedingungen' },
        { icon: Headphones, label: 'Gewährleistung', subtitle: 'Service & Support', to: '/gewaehrleistung' },
        { icon: Clock, label: 'Vorbestellen', subtitle: 'Ca. 60 Tage Lieferzeit', to: '/vorbestellen' },
        { icon: Users, label: 'Partner werden', subtitle: 'Jetzt bewerben', to: '/partner-werden' },
      ]
    },
    {
      title: 'Rechtliches',
      items: [
        { icon: FileText, label: 'AGB', to: '/agb' },
        { icon: FileText, label: 'Widerrufsbelehrung', to: '/widerrufsbelehrung' },
        { icon: CreditCard, label: 'Zahlungsarten', to: '/zahlungsarten' },
        { icon: FileText, label: 'Datenschutz', to: '/datenschutz' },
        { icon: FileText, label: 'Impressum', to: '/impressum' },
      ]
    },
    {
      title: 'Über uns',
      items: [
        { icon: Info, label: 'Über Jannesmann', subtitle: 'Unser Unternehmen', to: '/ueber-uns' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="flex items-center h-14 px-4">
          <button 
            onClick={() => navigate('/')}
            className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full active:bg-gray-100"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="flex-1 text-center font-bold text-lg text-gray-900">Mehr</h1>
          <div className="w-10" />
        </div>
      </header>

      {/* Menu Sections */}
      <div className="p-4 space-y-6">
        {menuSections.map((section) => (
          <div key={section.title}>
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
              {section.title}
            </h2>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
              {section.items.map((item, index) => {
                const Icon = item.icon;
                const isLast = index === section.items.length - 1;
                
                const content = (
                  <>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 ${
                      item.highlight ? 'bg-red-100' : 'bg-gray-100'
                    }`}>
                      <Icon size={20} className={item.highlight ? 'text-red-600' : 'text-gray-600'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${item.highlight ? 'text-red-600' : 'text-gray-900'}`}>
                        {item.label}
                      </p>
                      {item.subtitle && (
                        <p className="text-sm text-gray-500 truncate">{item.subtitle}</p>
                      )}
                    </div>
                    {item.external ? (
                      <ExternalLink size={18} className="text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
                    )}
                  </>
                );
                
                if (item.external) {
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      className={`flex items-center px-4 py-3.5 active:bg-gray-50 ${
                        !isLast ? 'border-b border-gray-100' : ''
                      }`}
                    >
                      {content}
                    </a>
                  );
                }
                
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`flex items-center px-4 py-3.5 active:bg-gray-50 ${
                      !isLast ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    {content}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {/* App Info */}
        <div className="text-center pt-4 pb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-3">
            <img 
              src="/icons/icon-96x96.png" 
              alt="Jannesmann" 
              className="w-12 h-12 object-contain"
            />
          </div>
          <p className="font-semibold text-gray-900">Jannesmann Baumaschinen</p>
          <p className="text-sm text-gray-500">App Version 2.0</p>
          <p className="text-xs text-gray-400 mt-2">© 2025 Jannesmann Baumaschinen</p>
        </div>
      </div>
    </div>
  );
};

export default PWAMoreMenu;
